import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = { code: "", label: "", description: "", priority_level: 3, color: "#64748B", estimated_service_time_minutes: 10, is_active: true };

export default function CategoriesPage() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const refresh = () => api.get("/categories").then((r) => setList(r.data));
  useEffect(() => { refresh(); }, []);

  const submit = async () => {
    try {
      const payload = { ...form, code: form.code.toUpperCase().slice(0, 4), priority_level: Number(form.priority_level) || 3, estimated_service_time_minutes: Number(form.estimated_service_time_minutes) || 10 };
      if (editing) { await api.patch(`/categories/${editing.id}`, payload); toast.success("Mis à jour"); }
      else { await api.post("/categories", payload); toast.success("Créé"); }
      setOpen(false); setEditing(null); setForm(EMPTY); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const onEdit = (c) => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); };
  const onDelete = async (c) => {
    if (!confirm(`Désactiver la catégorie ${c.code} ?`)) return;
    await api.delete(`/categories/${c.id}`); toast.success("Catégorie désactivée"); refresh();
  };

  return (
    <div className="space-y-6" data-testid="categories-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-3xl">Catégories de tickets</h1>
          <p className="text-slate-500 text-sm mt-1">Définissez les motifs de visite et leur priorité (1 = plus prioritaire).</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
          <DialogTrigger asChild>
            <Button className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="add-category-button">
              <Plus className="w-4 h-4 mr-1" /> Nouvelle catégorie
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Modifier" : "Nouvelle"} catégorie</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} maxLength={4} placeholder="SIN" /></div>
                <div><Label>Libellé</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Sinistre" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Priorité (1-9)</Label><Input type="number" min={1} max={9} value={form.priority_level} onChange={(e) => setForm({ ...form, priority_level: e.target.value })} /></div>
                <div><Label>Durée estimée (min)</Label><Input type="number" min={1} value={form.estimated_service_time_minutes} onChange={(e) => setForm({ ...form, estimated_service_time_minutes: e.target.value })} /></div>
              </div>
              <div><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Couleur</Label>
                  <div className="flex gap-2">
                    <Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="w-14 p-1 h-10" />
                    <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={submit} className="bg-[#DC2626] hover:bg-[#B91C1C]">Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {list.map((c) => (
          <div key={c.id} className="bg-white border border-slate-200 rounded-xl p-5"
            style={{ borderLeftWidth: 6, borderLeftColor: c.color }}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono font-bold text-xs uppercase tracking-widest text-slate-500">{c.code}</div>
                <div className="font-display font-bold text-xl">{c.label}</div>
                <div className="text-xs text-slate-500 mt-1">Priorité {c.priority_level} • {c.estimated_service_time_minutes} min</div>
              </div>
              <div className="flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => onEdit(c)}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => onDelete(c)}><Trash2 className="w-4 h-4 text-[#DC2626]" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
